import { useEffect, useState } from 'react';

import { IStore } from '@rbi-ctg/store';
import { useGetRestaurantsLazyQuery } from 'generated/graphql-gateway';
import {
  DeliveryStatus,
  DeliveryStoreStatus,
  OperationalStatus,
  ServiceMode,
} from 'generated/rbi-graphql';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useServiceModeContext } from 'state/service-mode';
import { convertMilesToMeters } from 'utils/distance';
import { IPlaceAddress } from 'utils/geolocation';
import { useGetAvailableRestaurantWithDetails } from 'utils/restaurant';

import { GroqArgs, groqQuery } from './groq';

const projection = `
  _id,
  chaseMerchantId,
  deliveryHours,
  diningRoomHours,
  curbsideHours,
  drinkStationType,
  driveThruHours,
  driveThruLaneType,
  email,
  fastestServiceMode,
  franchiseGroupId,
  franchiseGroupName,
  frontCounterClosed,
  hasBreakfast,
  hasBurgersForBreakfast,
  hasCurbside,
  hasDineIn,
  hasCatering,
  hasDelivery,
  hasDriveThru,
  hasMobileOrdering,
  hasParking,
  hasPlayground,
  hasTakeOut,
  hasWifi,
  hasLoyalty,
  isDarkKitchen,
  isHalal,
  latitude,
  longitude,
  mobileOrderingStatus,
  name,
  number,
  parkingType,
  phoneNumber,
  physicalAddress,
  playgroundType,
  pos,
  posRestaurantId,
  restaurantPosData->{_id},
  status,
  restaurantImage{..., asset->},
  vatNumber
`;

const storeQuery = `
  *[ _type == 'restaurant' && _id == $id ]{
    ${projection}
  }
`;

const storesByIdsWithOutLocationQuery = `
  *[ _type == 'restaurant' && _id in $storeIds ] {
    ${projection}
  }
`;

const storesByNumbersWithOutLocationQuery = `
  *[ _type == 'restaurant' && number in $storeNumbers ] {
    ${projection}
  }
`;

const storesByNumbersWithLocationQuery = `
*[ _type == 'restaurant' && number in $storeNumbers ] |
order(($userLat - latitude) ** 2 + ($userLng - longitude) ** 2) {
  ${projection}
}
`;

const storesByIdsQuery = `
  *[ _type == 'restaurant' && _id in $storeIds ] |
  order(($userLat - latitude) ** 2 + ($userLng - longitude) ** 2) {
    ${projection}
  }
`;

const storesByNumberQuery = `
  *[ _type == 'restaurant' && number == $storeNumber ] {
    ${projection}
  }
`;

interface IRestaurantQueryArgs {
  limit?: number;
  maxLat: number;
  maxLng: number;
  minLat: number;
  minLng: number;
  offset?: number;
  status: 'Open';
  userLat: number;
  userLng: number;
  hasDelivery?: boolean;
}

type ClosestAvailableRestaurantArgs = Pick<IRestaurantQueryArgs, 'userLat' | 'userLng'>;

export enum StoreStatus {
  OPEN = 'deliveryStoreOpen',
  CLOSED = 'deliveryStoresClosed',
  NO_DELIVERY = 'noDeliveryStores',
}

export declare interface IGetClosestAvailableDeliveryRestaurantResult {
  deliveryQuote: DeliveryStatus | null;
  storeStatus: StoreStatus;
  store: IStore | null;
  nextEarliestOpen?: Date;
}

export const DeliveryStoreStatusToStoreStatusMap: Record<DeliveryStoreStatus, StoreStatus> = {
  [DeliveryStoreStatus.CLOSED]: StoreStatus.CLOSED,
  [DeliveryStoreStatus.NO_DELIVERY]: StoreStatus.NO_DELIVERY,
  [DeliveryStoreStatus.OPEN]: StoreStatus.OPEN,
};

export type QueryClosestAvailableRestaurantTriple = [
  (
    phoneNumber: string,
    deliveryRadiusInMiles: number,
    { userLat, userLng }: ClosestAvailableRestaurantArgs,
    deliveryAddress?: IPlaceAddress | null
  ) => void,
  IGetClosestAvailableDeliveryRestaurantResult | undefined,
  string | undefined
];

const useCachedGetRestaurantsLazyQuery = withLazySimpleCache(useGetRestaurantsLazyQuery);

export const useQueryClosestAvailableDeliveryRestaurant = (): QueryClosestAvailableRestaurantTriple => {
  const [query, { data }] = useCachedGetRestaurantsLazyQuery();
  const { setDeliverySurchargeFee } = useServiceModeContext();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [storeData, setStoreData] = useState<
    IGetClosestAvailableDeliveryRestaurantResult | undefined
  >();
  const [address, setDeliveryAddress] = useState<IPlaceAddress | null | undefined>();
  const getAvailableRestaurantWithDetails = useGetAvailableRestaurantWithDetails();
  const { logEvent } = useMParticleContext();

  const getRestaurants = (
    phoneNumber: string,
    deliveryRadiusInMiles: number,
    { userLat, userLng }: ClosestAvailableRestaurantArgs,
    deliveryAddress?: IPlaceAddress | null
  ) => {
    setPhone(phoneNumber);
    setDeliveryAddress(deliveryAddress);
    query({
      allowFromCache: true,
      variables: {
        input: {
          coordinates: {
            searchRadius: convertMilesToMeters(deliveryRadiusInMiles),
            userLat,
            userLng,
          },
          radiusStrictMode: true,
          serviceModes: [ServiceMode.DELIVERY],
          status: OperationalStatus.OPEN,
        },
      },
    });
  };

  const restaurantNodes = data?.restaurants?.nodes;
  useEffect(() => {
    if (!restaurantNodes) {
      return;
    }

    const handleResults = async () => {
      const {
        deliveryQuoteError,
        storesWithInvalidHOO,
        deliveryQuoteSurchargeFeeCents,
        ...restaurantDetails
      } = await getAvailableRestaurantWithDetails(
        ((restaurantNodes ?? []) as unknown) as IStore[],
        phone,
        address
      );

      // Log stores which don't have delivery hours set
      storesWithInvalidHOO.forEach((store: IStore) => {
        logEvent(CustomEventNames.STORE_DELIVERY_INVALID_HOO, EventTypes.Other, {
          StoreId: store._id,
          DeliveryHours: store.deliveryHours,
        });
      });

      if (deliveryQuoteError) {
        setError(deliveryQuoteError);
      }
      if (deliveryQuoteSurchargeFeeCents) {
        setDeliverySurchargeFee(deliveryQuoteSurchargeFeeCents);
      }

      setStoreData(restaurantDetails as IGetClosestAvailableDeliveryRestaurantResult);
    };

    handleResults();
  }, [
    address,
    getAvailableRestaurantWithDetails,
    logEvent,
    phone,
    restaurantNodes,
    setDeliverySurchargeFee,
  ]);

  const result: QueryClosestAvailableRestaurantTriple = [getRestaurants, storeData, error];
  return result;
};

type GetRestaurantArgs = GroqArgs<{ id: string }>;

export const getRestaurant = (endpoint: string, { id }: GetRestaurantArgs) =>
  groqQuery<[IStore], GetRestaurantArgs>(endpoint, storeQuery, { id });

type GetRestaurantsByStoreNumberArgs = GroqArgs<{ storeNumber: string }>;

export const getRestaurantsByStoreNumber = (
  endpoint: string,
  { storeNumber }: GetRestaurantsByStoreNumberArgs
) =>
  groqQuery<IStore[], GetRestaurantsByStoreNumberArgs>(endpoint, storesByNumberQuery, {
    storeNumber,
  });

type GetRestaurantsByIdsArgs = {
  storeIds: string[];
  userLat: number;
  userLng: number;
};

type GetAllRestaurantsByIdsArgs = {
  storeIds: string[];
};

type GetAllRestaurantsByStoreNumbersArgs = {
  storeNumbers: string[];
  userLat?: number;
  userLng?: number;
};

export const getAllRestaurantsByIdsOrderedByLocation = (
  endpoint: string,
  { storeIds, userLat, userLng }: GroqArgs<GetRestaurantsByIdsArgs>
) =>
  groqQuery<IStore[], GetRestaurantsByIdsArgs>(endpoint, storesByIdsQuery, {
    storeIds,
    userLat,
    userLng,
  });

export const getAllRestaurantsByIds = (
  endpoint: string,
  { storeIds }: GroqArgs<GetAllRestaurantsByIdsArgs>
) =>
  groqQuery<IStore[], GetAllRestaurantsByIdsArgs>(endpoint, storesByIdsWithOutLocationQuery, {
    storeIds,
  });

export const getAllRestaurantsByStoreNumbers = (
  endpoint: string,
  { storeNumbers }: GroqArgs<GetAllRestaurantsByStoreNumbersArgs>
) =>
  groqQuery<IStore[], GetAllRestaurantsByStoreNumbersArgs>(
    endpoint,
    storesByNumbersWithOutLocationQuery,
    {
      storeNumbers,
    }
  );

export const getAllRestaurantsByStoreNumbersOrderedByLocation = (
  endpoint: string,
  { storeNumbers, userLat, userLng }: GroqArgs<GetAllRestaurantsByStoreNumbersArgs>
) =>
  groqQuery<IStore[], GetAllRestaurantsByStoreNumbersArgs>(
    endpoint,
    storesByNumbersWithLocationQuery,
    {
      storeNumbers,
      userLat,
      userLng,
    }
  );
